import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './Table.module.css';
import dayjs from 'dayjs';
import {
  InnocodeButton,
  InnocodeChip,
  InnocodeDatatable,
  InnocodeDropdown,
  InnocodeInput,
  openInnocodeDatePickerRange,
} from '@intsoft/innocode-core';
import { TableStateType } from '../../../utils/contextapi/TableProvider';

const searchOptions = [
  { value: 'tenantName', label: '테넌트명' },
  { value: 'createdByName', label: '소유주명' },
];

const TenantsStatusTable = ({
  datas,
  context,
  detailTenant,
  pageNumber,
  totalCount,
  listSize,
  searchValue,
  setSearchValue,
  onSearch,
  onPageChange,
  isMobileWindow,
}) => {
  const [searchType, setSearchType] = useState('tenantName');
  const [isInitialMount, setIsInitialMount] = useState(true);

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }

    onSearch(searchType, searchValue);
  }, [context.state.dateRange]);

  const formatDateRange = useCallback(() => {
    const [start, end] = context.state.dateRange;

    if (!start && !end) {
      return '날짜 범위 선택';
    }

    const startStr = start ? dayjs(start).format('YYYY-MM-DD') : '';
    const endStr = end ? dayjs(end).format('YYYY-MM-DD') : '';

    if (startStr && endStr) {
      return `${startStr} ~ ${endStr}`;
    }

    return startStr || endStr || '날짜 범위 선택';
  }, [context.state.dateRange]);

  const handleDateRangeClick = useCallback(async () => {
    const pickedDateRange = await openInnocodeDatePickerRange({
      initialDate: [context.state.dateRange[0], context.state.dateRange[1]],
      minDate: new Date(2020, 0, 1),
      maxDate: new Date(),
      mode: 'range',
    });

    const newStartDate = pickedDateRange?.startDate ? new Date(pickedDateRange.startDate) : null;
    const newEndDate = pickedDateRange?.endDate ? new Date(pickedDateRange.endDate) : null;

    context.actions.contextDispatch({
      type: TableStateType.DATE_RANGE,
      data: [newStartDate, newEndDate],
    });
  }, [context]);

  const getStatusBadge = useCallback((status) => {
    switch (status) {
      case 'approval':
        return <span className={styles.badgeSuccess}>활성화</span>;
      case 'disabled':
        return <span className={styles.badgeDanger}>비활성화</span>;
      case 'reject':
        return <span className={styles.badgeDanger}>거절</span>;
      default:
        return <span className={styles.badgeWarning}>승인대기</span>;
    }
  }, []);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        id: 'tenantName',
        header: '테넌트명',
        accessorKey: 'tenantName',
        enableSorting: false,
      },
    ];

    if (!isMobileWindow) {
      baseColumns.push({
        id: 'createdByName',
        header: '소유주명',
        accessorKey: 'createdByName',
        enableSorting: false,
      });

      baseColumns.push({
        id: 'status',
        header: '상태',
        accessorKey: 'status',
        enableSorting: false,
        cell: ({ row }) => getStatusBadge(row.status),
      });
    }

    baseColumns.push({
      id: 'createdDate',
      header: '등록일',
      accessorKey: 'createdDateView',
      enableSorting: false,
      cell: ({ row }) => (
        <span>{row.createdDateView ? dayjs(new Date(row.createdDateView)).format('YYYY-MM-DD HH:mm:ss') : '-'}</span>
      ),
    });

    return baseColumns;
  }, [getStatusBadge, isMobileWindow]);

  return (
    <div className={styles.tableSection}>
      <InnocodeDatatable
        className={styles.dataTable}
        data={datas}
        columns={columns}
        onRowClick={(row) => detailTenant(row.tenantId)}
        enableCustomHeader={true}
        customHeader={
          <div className={styles.customHeaderContainer}>
            <div className={styles.customHeaderSearchContainer}>
              <div className={styles.dropdownWrapper}>
                <InnocodeDropdown
                  value={searchType}
                  options={searchOptions}
                  onChange={(value) => setSearchType(value || 'tenantName')}
                />
              </div>
              <InnocodeInput
                className={styles.customHeaderSearchInput}
                placeholder="테넌트, 소유주 검색"
                showTrailingContent={true}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onSearch(searchType, searchValue);
                  }
                }}
                trailingContent={
                  <InnocodeButton
                    enablePadding={false}
                    size="xs"
                    theme="opacity"
                    designType="alternative"
                    iconOnly={true}
                    icon={
                      <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
                      </svg>
                    }
                    onClick={() => onSearch(searchType, searchValue)}
                  />
                }
              />
              <div className={styles.datePickerWrapper}>
                <InnocodeChip
                  size="lg"
                  theme="line"
                  type="primary"
                  onClick={handleDateRangeClick}
                  icon={
                    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                      <path fillRule="evenodd" d="M5 5a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1 2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2ZM3 19v-7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Zm6.01-6a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-10 4a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z" clipRule="evenodd"/>
                    </svg>
                  }
                >
                  {formatDateRange()}
                </InnocodeChip>
              </div>
            </div>
          </div>
        }
        enableTableHeader={true}
        enableSorting={false}
        enableCheckbox={false}
        enablePagination={true}
        manualPagination={true}
        pageSize={listSize}
        totalCount={totalCount}
        currentPage={pageNumber}
        onPageChange={onPageChange}
        emptyMessage="승인대기 테넌트가 없습니다."
        rowClassName={styles.tableRow}
      />
    </div>
  );
};

export default TenantsStatusTable;
